import React, { useState } from 'react'
import { Download, Mail, Share2, Copy, Check, AlertCircle, Loader2, Send } from 'lucide-react'

interface QuizResultActionsProps {
  quizResult: any
  profileData: any
  className?: string
}

export default function QuizResultActions({ quizResult, profileData, className = '' }: QuizResultActionsProps) {
  const [email, setEmail] = useState('')
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const shareUrl = `${window.location.origin}/share/${quizResult?.share_token || 'demo'}`

  const handleDownload = () => {
    try {
      const reportData = {
        title: 'Your DrizzID™ Flavor Profile Report',
        generatedAt: new Date().toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        shareUrl,
        profile: profileData,
        summary: {
          profileType: profileData.profileType,
          primaryGoal: profileData.primaryGoal || 'Balanced Experience',
          topStrengths: ['Intensity', 'Heat Tolerance', 'Flavor Complexity'].slice(0, 2)
        }
      }
      
      const jsonData = JSON.stringify(reportData, null, 2)
      const blob = new Blob([jsonData], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      
      const link = document.createElement('a')
      link.href = url
      link.download = `DrizzID-Profile-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      URL.revokeObjectURL(url)
    } catch (err) {
      setError('Failed to download report')
    }
  }

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (err) {
      setError('Failed to copy link')
    }
  }

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Simulate sending email
      await new Promise(resolve => setTimeout(resolve, 2000))
      setIsEmailSent(true)
      setShowEmailForm(false)
      setEmail('')
    } catch (err) {
      setError('Failed to send email')
    } finally {
      setIsLoading(false)
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My DrizzID™ Flavor Profile',
          text: `Check out my personalized flavor profile! I'm a ${profileData.profileType} with ${profileData.primaryGoal || 'balanced'} preferences.`,
          url: shareUrl
        })
      } catch (err) {
        handleCopyLink()
      }
    } else {
      handleCopyLink()
    }
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={handleDownload}
          className="flex items-center justify-center space-x-3 bg-gradient-to-r from-blue-500 to-cyan-600 text-white px-6 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105"
        >
          <Download className="h-5 w-5" />
          <span>Download Report</span>
        </button>

        <button
          onClick={() => setShowEmailForm(!showEmailForm)}
          className="flex items-center justify-center space-x-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-green-500/25 transition-all duration-300 hover:scale-105"
        >
          <Mail className="h-5 w-5" />
          <span>Email Results</span>
        </button>

        <button
          onClick={handleShare}
          className="flex items-center justify-center space-x-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white px-6 py-4 rounded-xl font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105"
        >
          <Share2 className="h-5 w-5" />
          <span>Share Profile</span>
        </button>
      </div>

      {/* Email Form */}
      {showEmailForm && (
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
            <Mail className="h-5 w-5 text-green-400" />
            <span>Email Your DrizzID™ Results</span>
          </h3>
          
          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                required
                disabled={isLoading}
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                type="submit"
                disabled={isLoading || !email}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    <span>Send Results</span>
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Share Link */}
      <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center space-x-2">
          <Share2 className="h-5 w-5 text-purple-400" />
          <span>Share Your Profile</span>
        </h3>
        
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={shareUrl}
            readOnly
            className="flex-1 px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-gray-300 text-sm"
          />
          <button
            onClick={handleCopyLink}
            className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
          >
            {isCopied ? (
              <>
                <Check className="h-4 w-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span>Copy</span>
              </>
            )}
          </button>
        </div>
        
        <p className="text-gray-400 text-sm mt-3">
          Share this link with friends to show them your DrizzID™ flavor profile
        </p>
      </div>

      {/* Success Messages */}
      {isEmailSent && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center space-x-3">
          <Check className="h-5 w-5 text-green-400 flex-shrink-0" />
          <div>
            <p className="text-green-300 font-semibold">Email sent successfully!</p>
            <p className="text-green-400 text-sm">Check your inbox for your DrizzID™ results</p>
          </div>
        </div>
      )}

      {/* Error Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center space-x-3">
          <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-red-300 font-semibold">Error</p>
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Download Info */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
        <h4 className="text-blue-300 font-semibold mb-2">What's included in your report?</h4>
        <ul className="text-blue-400 text-sm space-y-1">
          <li>• Complete DrizzID™ flavor profile analysis</li>
          <li>• Personalized ingredient recommendations</li>
          <li>• Detailed taste preference breakdown</li>
          <li>• Shareable profile link</li>
          <li>• AI-generated insights and suggestions</li>
        </ul>
      </div>
    </div>
  )
}